import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import {
  CheckContractStatusResponse,
  CHECK_CONTRACT_STATUS,
} from '../gql/contracts'
import { Creator } from '../gql/types'
import { useNotification } from '../utils/hooks'
import { NotificationType } from '../components'

interface UseContractProtectionOptions {
  creator: Creator | null | undefined
  onContractRequired?: () => void
  skipCheck?: boolean
}

interface ContractProtectionResult {
  needsToSignContract: boolean
  hasActiveContract: boolean
  loading: boolean
  error: any
  checkingContract: boolean
}

/**
 * Hook to check if a creator needs to sign the latest contract
 * Returns contract status and provides callbacks for handling contract requirements
 */
export const useContractProtection = ({
  creator,
  onContractRequired,
  skipCheck = false,
}: UseContractProtectionOptions): ContractProtectionResult => {
  const { addNotification } = useNotification()
  const [checkingContract, setCheckingContract] = useState(false)

  const { data, loading, error, refetch } =
    useQuery<CheckContractStatusResponse>(CHECK_CONTRACT_STATUS, {
      variables: { creatorId: creator?.id },
      skip: !creator?.id || skipCheck,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network', // Always check for latest contract status
    })

  const contractStatus = data?.checkCreatorContractStatus
  const hasActiveContract = contractStatus?.hasSignedActiveContract ?? false
  const needsToSignContract = !hasActiveContract

  useEffect(() => {
    if (!loading && !error && needsToSignContract && onContractRequired) {
      setCheckingContract(true)

      // Show notification about contract requirement
      addNotification(
        'You need to accept our updated terms of service to continue using Clip.',
        NotificationType.error,
      )

      // Call the callback to handle contract requirement (e.g., redirect)
      onContractRequired()

      setCheckingContract(false)
    }
  }, [needsToSignContract, loading, error, onContractRequired, addNotification])

  useEffect(() => {
    if (error) {
      console.error('Contract status check failed:', error)
      // Don't block access if we can't check contract status
      // This ensures the app doesn't break if the contract service is down
    }
  }, [error])

  return {
    needsToSignContract,
    hasActiveContract,
    loading,
    error,
    checkingContract,
  }
}

/**
 * Hook specifically for blocking access until contract is signed
 * Returns true if the user should be blocked from accessing the content
 */
export const useContractBlocker = (
  creator: Creator | null | undefined,
): {
  shouldBlock: boolean
  loading: boolean
  refetchContractStatus: () => void
} => {
  const [shouldBlock, setShouldBlock] = useState(false)

  const { needsToSignContract, loading, error } = useContractProtection({
    creator,
    skipCheck: !creator?.id,
  })

  // Only block if we successfully checked and creator needs to sign
  // Don't block on errors to prevent app breaking
  useEffect(() => {
    if (!loading && !error) {
      setShouldBlock(needsToSignContract)
    } else if (error) {
      // On error, don't block access
      setShouldBlock(false)
    }
  }, [needsToSignContract, loading, error])

  const refetchContractStatus = async () => {
    // Force refetch of contract status
    try {
      window.location.reload() // Simple way to refresh contract status
    } catch (err) {
      console.error('Failed to refetch contract status:', err)
    }
  }

  return {
    shouldBlock,
    loading,
    refetchContractStatus,
  }
}
